import { formatDioptre, toNumber } from './dioptre.js';
/**
 * The job ticket a lab is sent when it is not integrated. §6.9 `JOB-011`, Appendix L, §15.3.
 *
 * `JOB-011` `MUST`: "Manual fallback for every lab: a printable/PDF job ticket and an emailable
 * order that a lab can key in. Most of the world's labs are not integrated, and the product must
 * work anyway." `V1-SPEC.md` §2 excludes lab EDI and VCA/OMA, which is `JOB-010`. **This is the
 * path that replaces them**, so it is not a nice-to-have in v1: it is the only way a job reaches a
 * lab at all.
 *
 * Nothing built it. `job` and `job_eye_snapshot` have held the data since the schema was written,
 * `supplier` has a name, an email and a transmission channel, and there was no ticket: no builder,
 * no route, no screen. A job could be created, sent and collected, and nobody could tell the lab
 * what to make.
 *
 * ---
 *
 * **Appendix L fixes the content, which is why this is a domain module and not a component.** Its
 * row for the job ticket:
 *
 * > Job number and barcode · frozen Rx snapshot · frame with colour and size · lens specification
 * > with treatments · all fitting measurements · special instructions · lab and promised date ·
 * > **designed to be photographed on one phone and read on another**
 *
 * That last clause is a layout requirement and belongs to the stylesheet. The rest is a list of
 * fields, and a list of mandatory fields is a compliance statement: if it lives in a component, the
 * next component drops one.
 *
 * **Three things happen here that a renderer cannot do.**
 *
 * First, it refuses a ticket with no eyes. A job with no `job_eye_snapshot` row is a job nobody
 * can make, and a sheet of paper with a frame on it and no powers is worse than no sheet, because
 * somebody will grind something from it.
 *
 * Second, the values come **only from the frozen snapshot**, never from the live prescription.
 * `JOB-002` freezes the Rx, frame, lens spec and measurements at order time precisely so a later
 * correction does not rewrite what was manufactured, and a ticket that read the current Rx would
 * undo that at the one moment it matters. The caller passes the snapshot; this has no way to reach
 * anything else.
 *
 * Third, it says what Appendix L asks for and this job cannot supply, in `absent`, rather than
 * leaving a line off. A job with no supplier, a rimless frame with no mounting notes, a job with no
 * promised date: all real, all gaps, and the list is what carries them to whoever is reading the
 * output instead of a ticket that looks complete.
 *
 * **No money, ever.** Appendix L's job ticket row lists no price, and the frame label row lists one,
 * which is the distinction. A lab document carrying retail price or cost is a margin leak to a
 * supplier who quotes against it, and there is no field here to put one in.
 *
 * No I/O, no formatting beyond signed dioptres, no barcode: the Code 128 modules are a renderer's
 * business, and `renderJobOrderText` below is the plain-text body of the emailable order, which is
 * a string and not a document.
 */
export class JobTicketError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'JobTicketError';
    }
}
const ORDER = { od: 0, os: 1 };
/**
 * Every dioptric value on one eye, signed and to two decimals.
 *
 * `RX-03`: the sign is always rendered, including `+`. Two decimals always, because a column of
 * powers aligns on the decimal (`TY-04`), and because `-2` keyed as `-2` where `-2.00` was meant is
 * the kind of ambiguity a lab resolves by telephoning.
 *
 * The axis and the diameter stay numbers: an axis is degrees, a diameter is millimetres, and
 * neither is signed or quarter-stepped.
 */
const millimetres = (value) => value.toFixed(2);
const formatEye = (eye) => ({
    eye: eye.eye,
    sphere: formatDioptre(eye.sphere),
    cylinder: formatDioptre(eye.cylinder),
    ...(eye.axis === undefined ? {} : { axis: eye.axis }),
    ...(eye.addPower === undefined ? {} : { addPower: formatDioptre(eye.addPower) }),
    ...(eye.prismHorizontal === undefined
        ? {}
        : { prismHorizontal: formatDioptre(eye.prismHorizontal) }),
    ...(eye.prismBaseHorizontal === undefined
        ? {}
        : { prismBaseHorizontal: eye.prismBaseHorizontal }),
    ...(eye.prismVertical === undefined ? {} : { prismVertical: formatDioptre(eye.prismVertical) }),
    ...(eye.prismBaseVertical === undefined ? {} : { prismBaseVertical: eye.prismBaseVertical }),
    ...(eye.monocularPdMm === undefined ? {} : { monocularPdMm: millimetres(eye.monocularPdMm) }),
    ...(eye.segmentHeightMm === undefined
        ? {}
        : { segmentHeightMm: millimetres(eye.segmentHeightMm) }),
    ...(eye.lensProductName === undefined ? {} : { lensProductName: eye.lensProductName }),
    ...(eye.treatmentNames === undefined ? {} : { treatmentNames: eye.treatmentNames }),
    ...(eye.diameterMm === undefined ? {} : { diameterMm: eye.diameterMm }),
    ...(eye.baseCurve === undefined ? {} : { baseCurve: formatDioptre(eye.baseCurve) }),
});
export function buildJobTicket(request) {
    if (request.eyes.length === 0) {
        throw new JobTicketError('JOB_TICKET_NO_EYES', 'This job has no frozen prescription, so there is nothing for a lab to make. A ticket with a frame and no powers is one somebody grinds anyway.');
    }
    if (request.jobNumber.trim() === '') {
        throw new JobTicketError('JOB_TICKET_NO_NUMBER', 'A job ticket is identified by its job number, and this job has none. Appendix L puts it on the barcode and in human-readable form beneath.');
    }
    for (const eye of request.eyes) {
        /**
         * An axis is meaningless without a cylinder and mandatory with one. `RX-002` states it as a
         * validation rule, and the reason it is re-checked at the ticket is that this is the last
         * moment before a lens is ground: a cylinder with no axis is a lens made at the wrong angle,
         * and the lab cannot tell that the field was missing rather than zero.
         */
        if (toNumber(eye.cylinder) !== 0 && eye.axis === undefined) {
            throw new JobTicketError('JOB_TICKET_AXIS_MISSING', `The ${eye.eye.toUpperCase()} snapshot has a cylinder and no axis, so a lab would grind it at whatever angle it guessed.`);
        }
    }
    const absent = [];
    if (request.lab === undefined) {
        absent.push('lab');
    }
    else if (request.lab.email === undefined && request.lab.phone === undefined) {
        // Appendix L names the lab; a lab with no way to reach it is a ticket that can be printed and
        // not sent, which is the half of `JOB-011` that matters more.
        absent.push('a way to reach the lab');
    }
    if (request.promisedOn === undefined)
        absent.push('promised date');
    if (request.specialInstructions === undefined)
        absent.push('special instructions');
    if (request.frame.kind === 'ambiguous') {
        absent.push(`frame (this order has ${request.frame.count} and none is recorded against this job)`);
    }
    if (request.frame.kind === 'shop') {
        if (request.frame.eyeSizeMm === undefined || request.frame.bridgeMm === undefined) {
            absent.push('frame size');
        }
        if (request.frame.colourCode === undefined)
            absent.push('frame colour');
        /**
         * `CAT-003`: a rimless or supra frame cannot be glazed without knowing how it mounts, and
         * `assertSendableToLab` already refuses to send one without them. Reported rather than refused
         * here, so a ticket printed before the job is sent still prints, and says what is missing.
         */
        if (request.frame.needsMountingData === true && request.frame.mountingNotes === undefined) {
            absent.push('mounting data for a rimless or supra frame');
        }
    }
    const eyesWithoutLens = request.eyes.filter((eye) => eye.lensProductName === undefined);
    if (eyesWithoutLens.length > 0) {
        absent.push(`lens specification for ${eyesWithoutLens.map((eye) => eye.eye.toUpperCase()).join(' and ')}`);
    }
    if (request.eyes.some((eye) => eye.monocularPdMm === undefined)) {
        // Appendix L: "all fitting measurements". A PD is the one no lens can be centred without.
        absent.push('monocular PD');
    }
    if (request.eyes.some((eye) => eye.treatmentsKnown !== true)) {
        /**
         * Appendix L asks for "lens specification **with** treatments", and an unknown treatment list
         * is not an empty one. Said on the ticket so the lab asks, rather than coating nothing and
         * sending back a lens the customer paid a coating for.
         */
        absent.push('treatments (not recorded against this job)');
    }
    // `od` before `os`, always, which is how every prescription in the product is written and how a
    // lab reads one. Sorting by whatever order the rows came back from a database would put the left
    // eye first on some tickets and not others.
    const eyes = [...request.eyes]
        .sort((a, b) => (ORDER[a.eye] ?? 9) - (ORDER[b.eye] ?? 9))
        .map(formatEye);
    return {
        jobNumber: request.jobNumber,
        barcodeValue: request.jobNumber,
        shopName: request.shopName,
        ...(request.branchName === undefined ? {} : { branchName: request.branchName }),
        ...(request.lab === undefined ? {} : { lab: request.lab }),
        ...(request.promisedOn === undefined ? {} : { promisedOn: request.promisedOn }),
        frame: request.frame,
        eyes,
        ...(request.specialInstructions === undefined
            ? {}
            : { specialInstructions: request.specialInstructions }),
        absent,
    };
}
/**
 * The emailable order, as plain text. `JOB-011`'s second half.
 *
 * **Plain text and not a layout**, because it is pasted into a mail body and keyed in by somebody
 * at the other end. A lab clerk reading a wrapped HTML table transcribes it wrongly; a labelled
 * line per field does not wrap into a different meaning.
 *
 * Every power carries its sign through `formatDioptre`, which `RX-03` requires and which matters
 * more here than anywhere: `-2.00` keyed as `2.00` is a lens made at the opposite power, and it is
 * the single most expensive transcription error in the trade.
 *
 * Western digits, always, including for a shop running in Urdu. `LOC-005` puts dioptric values and
 * numbers outside the mirroring and the localisation, and a lab reading Eastern Arabic numerals off
 * a photographed sheet is a remake.
 */
export function renderJobOrderText(ticket) {
    const lines = [];
    lines.push(`JOB ${ticket.jobNumber}`);
    lines.push(ticket.branchName === undefined ? ticket.shopName : `${ticket.shopName} (${ticket.branchName})`);
    if (ticket.promisedOn !== undefined)
        lines.push(`Promised: ${ticket.promisedOn}`);
    lines.push('');
    if (ticket.frame.kind === 'own') {
        // Said first and said plainly: this frame belongs to the customer and a lab that ruins it has
        // ruined something the shop cannot replace (`CAT-007`).
        lines.push("FRAME: CUSTOMER'S OWN, HANDLE WITH CARE");
        lines.push(ticket.frame.description);
    }
    else if (ticket.frame.kind === 'ambiguous') {
        // Named rather than left out, because a lab that sees no frame section assumes the shop is
        // sending one and waits for it.
        lines.push(`FRAME: NOT IDENTIFIED (this order has ${ticket.frame.count})`);
    }
    else {
        const frame = ticket.frame;
        const name = [frame.brand, frame.model].filter((part) => part !== undefined).join(' ');
        lines.push(`FRAME: ${name}`);
        if (frame.colourCode !== undefined)
            lines.push(`Colour: ${frame.colourCode}`);
        const size = [frame.eyeSizeMm, frame.bridgeMm, frame.templeMm]
            .filter((part) => part !== undefined)
            .join('-');
        if (size !== '')
            lines.push(`Size: ${size}`);
        if (frame.mountingNotes !== undefined)
            lines.push(`Mounting: ${frame.mountingNotes}`);
    }
    lines.push('');
    for (const eye of ticket.eyes) {
        lines.push(`${eye.eye.toUpperCase()}`);
        // Already signed and to two decimals by `formatEye`, so the text renderer and the screen print
        // the identical string rather than formatting the same value twice.
        lines.push(`  Sphere ${eye.sphere}  Cyl ${eye.cylinder}` +
            (eye.axis === undefined ? '' : `  Axis ${eye.axis}`));
        if (eye.addPower !== undefined)
            lines.push(`  Add ${eye.addPower}`);
        if (eye.prismHorizontal !== undefined) {
            lines.push(`  Prism ${eye.prismHorizontal} base ${eye.prismBaseHorizontal ?? '?'}`);
        }
        if (eye.prismVertical !== undefined) {
            lines.push(`  Prism ${eye.prismVertical} base ${eye.prismBaseVertical ?? '?'}`);
        }
        if (eye.monocularPdMm !== undefined)
            lines.push(`  PD ${eye.monocularPdMm} mm`);
        if (eye.segmentHeightMm !== undefined)
            lines.push(`  Seg height ${eye.segmentHeightMm} mm`);
        if (eye.lensProductName !== undefined)
            lines.push(`  Lens: ${eye.lensProductName}`);
        if (eye.treatmentNames !== undefined && eye.treatmentNames.length > 0) {
            lines.push(`  Treatments: ${eye.treatmentNames.join(', ')}`);
        }
        if (eye.diameterMm !== undefined)
            lines.push(`  Diameter ${eye.diameterMm} mm`);
        if (eye.baseCurve !== undefined)
            lines.push(`  Base curve ${eye.baseCurve}`);
        lines.push('');
    }
    if (ticket.specialInstructions !== undefined) {
        lines.push('SPECIAL INSTRUCTIONS');
        lines.push(ticket.specialInstructions);
        lines.push('');
    }
    if (ticket.absent.length > 0) {
        // On the order itself, because the lab is the one who finds out otherwise, and they find out by
        // making the wrong thing or by telephoning. Named as missing rather than left blank.
        lines.push(`NOT SUPPLIED: ${ticket.absent.join('; ')}`);
    }
    return lines.join('\n');
}
//# sourceMappingURL=job-ticket.js.map